import { gql } from '@apollo/client';
import {
  DashboardFolderFieldsFragment,
  DashboardImageEmbedUploadFieldsFragment,
  DashboardInstanceFieldsFragment,
  DashboardTemplateFieldsFragment,
} from './fragments';
import { EntityPermissionFieldsFragment, ErrorFragment } from '../fragments';
import { RichTextFieldsFragment } from '../richText/fragments';
import { ExternalEmbedFieldsFragment } from '../externalEmbed/fragments';
import { AnalysisSummaryFieldsFragment } from '../analysis/fragments';

export const CREATE_DASHBOARD = gql`
  mutation dashboardCreate($input: DashboardCreateInput!) {
    dashboardCreate(input: $input) {
      entity {
        ...DashboardTemplateFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardTemplateFieldsFragment}
  ${ErrorFragment}
`;

export const dashboardAddAnalysesMutation = gql`
  mutation dashboardAddAnalyses($input: DashboardAddAnalysesInput!) {
    dashboardAddAnalyses(input: $input) {
      entity {
        ...DashboardInstanceFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardInstanceFieldsFragment}
  ${ErrorFragment}
`;

export const dashboardSetFilterMutation = gql`
  mutation dashboardSetFilter($input: DashboardSetFilterInput!) {
    dashboardSetFilter(input: $input) {
      entity {
        ...DashboardInstanceFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardInstanceFieldsFragment}
  ${ErrorFragment}
`;

export const DELETE_DASHBOARD = gql`
  mutation dashboardDelete($input: DashboardDeleteInput!) {
    dashboardDelete(input: $input) {
      message
      errors {
        ...ErrorFields
      }
    }
  }
  ${ErrorFragment}
`;

export const MOVE_DASHBOARD = gql`
  mutation dashboardMove($input: DashboardMoveInput!) {
    dashboardMove(input: $input) {
      entity {
        ...DashboardTemplateFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardTemplateFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_FOLDER_CREATE = gql`
  mutation dashboardFolderCreate($input: SubFolderCreateInput!) {
    dashboardFolderCreate(input: $input) {
      entity {
        ...DashboardFolderFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardFolderFieldsFragment}
  ${ErrorFragment}
`;

export const dashboardFolderDeleteMutation = gql`
  mutation dashboardFolderDelete($input: SubFolderDeleteInput!) {
    dashboardFolderDelete(input: $input) {
      message
      errors {
        ...ErrorFields
      }
    }
  }
  ${ErrorFragment}
`;

export const dashboardFolderRenameMutation = gql`
  mutation dashboardFolderRename($input: SubFolderRenameInput!) {
    dashboardFolderRename(input: $input) {
      entity {
        ...DashboardFolderFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardFolderFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_REPLACE_ANALYSIS_MUTATION = gql`
  mutation dashboardReplaceAnalysis($input: DashboardReplaceAnalysisInput!) {
    dashboardReplaceAnalysis(input: $input) {
      entity {
        ...DashboardInstanceFields
      }
      newAnalysisId
      warningMessage
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardInstanceFieldsFragment}
  ${ErrorFragment}
`;

export const CLONE_DASHBOARD = gql`
  mutation dashboardClone($input: DashboardCloneInput!) {
    dashboardClone(input: $input) {
      entity {
        ...DashboardTemplateFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardTemplateFieldsFragment}
  ${ErrorFragment}
`;

export const UNPUBLISH_DASHBOARD = gql`
  mutation dashboardUnpublish($input: DashboardUnpublishInput!) {
    dashboardUnpublish(input: $input) {
      entity {
        ...DashboardTemplateFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardTemplateFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_EXECUTE = gql`
  mutation dashboardExecute($input: DashboardExecuteInput!) {
    dashboardExecute(input: $input) {
      dashboard {
        ...DashboardInstanceFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardInstanceFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_EDIT_ENTITY_PERMISSIONS = gql`
  mutation dashboardEntityPermissionEdit($input: EntityPermissionEditInput!) {
    entityPermissionEdit(input: $input) {
      entity {
        ...EntityPermissionFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${EntityPermissionFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_REFRESH = gql`
  mutation dashboardRefresh($input: DashboardRefreshInput!) {
    dashboardRefresh(input: $input) {
      dashboard {
        ...DashboardInstanceFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardInstanceFieldsFragment}
  ${ErrorFragment}
`;

export const VISIT_DASHBOARD = gql`
  mutation dashboardVisit($input: DashboardVisitInput!) {
    dashboardVisit(input: $input) {
      errors {
        ...ErrorFields
      }
      visit {
        baseId
      }
    }
  }
  ${ErrorFragment}
`;

export const DASHBOARD_DUPLICATE_ANALYSIS = gql`
  mutation dashboardDuplicateAnalysis(
    $input: DashboardDuplicateAnalysisInput!
  ) {
    dashboardDuplicateAnalysis(input: $input) {
      entity {
        ...DashboardInstanceFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardInstanceFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_NAME_EDIT = gql`
  mutation dashboardNameEdit($input: DashboardNameEditInput!) {
    dashboardNameEdit(input: $input) {
      entity {
        ...DashboardInstanceFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${DashboardInstanceFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_GRID_UPDATE = gql`
  mutation dashboardGridUpdate($input: DashboardGridUpdateInput!) {
    dashboardGridUpdate(input: $input) {
      errors {
        ...ErrorFields
      }
      entity {
        ...DashboardInstanceFields
      }
    }
  }
  ${DashboardInstanceFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_RICH_TEXT_ADD = gql`
  mutation dashboardRichTextAdd($input: DashboardRichTextAddInput!) {
    dashboardRichTextAdd(input: $input) {
      errors {
        ...ErrorFields
      }
      richText {
        ...RichTextFields
      }
    }
  }
  ${RichTextFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_RICH_TEXT_EDIT = gql`
  mutation dashboardRichTextEdit($input: DashboardRichTextEditInput!) {
    dashboardRichTextEdit(input: $input) {
      errors {
        ...ErrorFields
      }
      richText {
        ...RichTextFields
      }
    }
  }
  ${RichTextFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_EXTERNAL_EMBED_ADD = gql`
  mutation dashboardExternalEmbedAdd($input: DashboardExternalEmbedAddInput!) {
    dashboardExternalEmbedAdd(input: $input) {
      errors {
        ...ErrorFields
      }
      externalEmbed {
        ...ExternalEmbedFields
      }
    }
  }
  ${ExternalEmbedFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_EXTERNAL_EMBED_EDIT = gql`
  mutation dashboardExternalEmbedEdit(
    $input: DashboardExternalEmbedEditInput!
  ) {
    dashboardExternalEmbedEdit(input: $input) {
      errors {
        ...ErrorFields
      }
      externalEmbed {
        ...ExternalEmbedFields
      }
    }
  }
  ${ExternalEmbedFieldsFragment}
  ${ErrorFragment}
`;

export const DASHBOARD_IMAGE_EMBED_ADD = gql`
  mutation dashboardImageEmbedAdd(
    $input: DashboardImageEmbedPostUploadConfigInput!
  ) {
    dashboardImageEmbedAdd(input: $input) {
      uploadConfig {
        ...DashboardImageEmbedUploadConfigFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${ErrorFragment}
  ${DashboardImageEmbedUploadFieldsFragment}
`;

export const DASHBOARD_IMAGE_EMBED_EDIT = gql`
  mutation dashboardImageEmbedEdit($input: DashboardImageEmbedEditInput!) {
    dashboardImageEmbedEdit(input: $input) {
      uploadConfig {
        ...DashboardImageEmbedUploadConfigFields
      }
      errors {
        ...ErrorFields
      }
    }
  }
  ${ErrorFragment}
  ${DashboardImageEmbedUploadFieldsFragment}
`;

export const DASHBOARD_ANALYSIS_ADD = gql`
  mutation dashboardAnalysisAdd($input: DashboardAnalysisAddInput!) {
    dashboardAnalysisAdd(input: $input) {
      errors {
        ...ErrorFields
      }
      analysis {
        ...AnalysisSummaryInfoFields
      }
    }
  }
  ${AnalysisSummaryFieldsFragment}
  ${ErrorFragment}
`;
